import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, User } from "lucide-react";
import { Logo } from "./Logo";
import { SITE } from "../config/site";
import { scrollToId } from "../lib/scroll";
import { track } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";
import { useBookingUrl, useContent } from "../context/ContentContext";

const NAV = [
  { label: "About", hash: "#meet-staci", id: "about" },
  { label: "Approach", hash: "#approach", id: "approach" },
  { label: "Consultations", hash: "#consultations", id: "consultations" },
  { label: "FAQs", hash: "#faq", id: "faqs" },
  { label: "Contact", hash: "#contact", id: "contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { member } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const bookingUrl = useBookingUrl();
  const ctaLabel = useContent("hero_cta_primary", "Book a Session");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") navigate("/" + hash);
    else scrollToId(hash);
  };

  const memberLink = member
    ? { to: "/my-wellness-journey", label: "My Wellness Journey", id: "my-wellness-journey" }
    : { to: "/login", label: "Member Login", id: "member-login" };

  const RESOURCES = [
    { to: "/terrain-and-truth", label: "Terrain and Truth", id: "terrain-and-truth" },
    { to: "/the-detox-gospel", label: "The Detox Gospel", id: "the-detox-gospel" },
    { to: "/favorites", label: "Staci's Favorites", id: "favorites" },
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled
          ? "bg-ivory/85 backdrop-blur-xl border-forest/10 shadow-[0_8px_30px_rgba(23,63,53,0.06)]"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-[76px] items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
            {NAV.map((item) => (
              <a
                key={item.id}
                href={item.hash}
                data-testid={`nav-link-${item.id}`}
                onClick={go(item.hash)}
                className="text-[13px] tracking-wide text-charcoal/80 hover:text-forest transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-gold after:transition-[width] after:duration-300 hover:after:w-full"
              >
                {item.label}
              </a>
            ))}
            <Link
              to="/wellness-discovery"
              data-testid="nav-link-wellness-discovery"
              className="text-[13px] font-semibold tracking-wide text-forest hover:text-gold-dark transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-gold after:transition-[width] after:duration-300 hover:after:w-full"
            >
              Wellness Discovery
            </Link>
            <div className="relative group">
              <button
                data-testid="nav-resources"
                aria-haspopup="true"
                className="inline-flex items-center gap-1 text-[13px] tracking-wide text-charcoal/80 hover:text-forest transition-colors duration-300"
              >
                Resources
                <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
              </button>
              <div className="invisible absolute left-1/2 top-full -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                <div className="flex w-60 flex-col gap-1 rounded-2xl border border-forest/10 bg-ivory/95 p-2.5 shadow-[0_18px_45px_rgba(23,63,53,0.15)] backdrop-blur-xl">
                  {RESOURCES.map((r) => (
                    <Link
                      key={r.id}
                      to={r.to}
                      data-testid={`nav-link-${r.id}`}
                      className="rounded-xl px-4 py-2.5 text-[13px] font-semibold text-forest transition-colors hover:bg-sage"
                    >
                      {r.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to={memberLink.to}
              data-testid={`nav-${memberLink.id}`}
              className="hidden lg:inline-flex items-center gap-2 rounded-full border border-forest/20 px-5 py-2.5 text-[13px] font-semibold text-forest transition-all duration-300 hover:border-gold hover:text-gold-dark"
            >
              <User className="h-4 w-4" />
              {memberLink.label}
            </Link>
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-book-session-button"
              onClick={() => track("booking_click", { location: "header" })}
              className="hidden sm:inline-flex items-center rounded-full bg-forest px-6 py-2.5 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep hover:shadow-[0_10px_25px_rgba(23,63,53,0.25)] hover:-translate-y-0.5"
            >
              {ctaLabel}
            </a>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-forest/15 text-forest"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-ivory/95 backdrop-blur-xl border-t border-forest/10"
            aria-label="Mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              <Link
                to="/wellness-discovery"
                data-testid="mobile-nav-link-wellness-discovery"
                onClick={() => setOpen(false)}
                className="font-serif text-2xl text-gold-dark"
              >
                Wellness Discovery
              </Link>
              {NAV.map((item) => (
                <a
                  key={item.id}
                  href={item.hash}
                  data-testid={`mobile-nav-link-${item.id}`}
                  onClick={go(item.hash)}
                  className="font-serif text-2xl text-forest"
                >
                  {item.label}
                </a>
              ))}
              <div className="border-t border-forest/10 pt-4">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
                  Resources
                </p>
                <div className="mt-3 flex flex-col gap-4">
                  {RESOURCES.map((r) => (
                    <Link
                      key={r.id}
                      to={r.to}
                      data-testid={`mobile-nav-link-${r.id}`}
                      onClick={() => setOpen(false)}
                      className="font-serif text-xl text-forest"
                    >
                      {r.label}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <Link
                  to={memberLink.to}
                  data-testid={`mobile-nav-${memberLink.id}`}
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-6 py-3 text-sm font-semibold text-forest"
                >
                  <User className="h-4 w-4" />
                  {memberLink.label}
                </Link>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-book-session-button"
                  onClick={() => track("booking_click", { location: "mobile-menu" })}
                  className="inline-flex w-fit items-center rounded-full bg-forest px-7 py-3 text-sm font-semibold text-ivory"
                >
                  {ctaLabel}
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
